// The standalone/production embed uses the primary id; a second id lets
// another shortcode (e.g. a not-yet-promoted preview build) mount on the
// same site without colliding with the live one's element id.
const ROOT_ELEMENT_IDS = [
    'callcenter-booking-system',
    'callcenter-booking-system-dynamic',
];

/**
 * Finds whichever of the known root element ids is actually present on the
 * page — a host site only ever renders one of them for a given shortcode.
 */
export const getRootElement = (): HTMLElement | null => {
    for (const id of ROOT_ELEMENT_IDS) {
        const el = document.getElementById(id);
        if (el) {
            return el;
        }
    }
    return null;
};

export const getCorpCode = () => {
    // Primary path for the internal call-center tool: agents switch between
    // corps via a `?corp=` link, so onboarding a new corp needs zero code
    // changes or redeploys — just automate/mtas config for that corp code.
    const corpFromQuery = new URLSearchParams(window.location.search).get('corp');
    if (corpFromQuery) {
        return corpFromQuery;
    }

    // Local dev convenience only — import.meta.env.DEV is false in any built
    // bundle (including a local production build), so this can never
    // accidentally activate outside of `vite dev`.
    if (import.meta.env.DEV && import.meta.env.VITE_CORP_CODE) {
        return import.meta.env.VITE_CORP_CODE;
    }

    // Fallback for a corp embedding this as a widget on their own site, where
    // the host page sets its own data-CorpCode attribute.
    const root = getRootElement();
    if (!root) {
        throw new Error('Root element not found');
    }

    return root.getAttribute('data-CorpCode');
};

/**
 * Same fallback pattern as `getCorpCode` — an embedded widget can't rely on
 * `?locationCode=` in the host page's own URL (that'd require whoever adds
 * the shortcode to hand-construct a query string), so a `data-LocationCode`
 * attribute on the same root element lets it preselect a location too.
 */
export const getLocationCode = () => {
    const locationCodeFromQuery = new URLSearchParams(window.location.search).get(
        'locationCode'
    );
    if (locationCodeFromQuery) {
        return locationCodeFromQuery;
    }

    return getRootElement()?.getAttribute('data-LocationCode') ?? null;
};
